import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';

// Small app-wide toasts and a styled confirm dialog.
// notify('Saved') / notify.error('Could not save') from anywhere; confirmAction({...}) returns a Promise<boolean>.

const TOAST_EVENT = 'ft-toast';
const CONFIRM_EVENT = 'ft-confirm';

export function notify(message, type = 'success') {
    window.dispatchEvent(new CustomEvent(TOAST_EVENT, { detail: { message, type } }));
}
notify.success = (message) => notify(message, 'success');
notify.error = (message) => notify(message, 'error');
notify.info = (message) => notify(message, 'info');

// Pulls the server's error text out of an axios error, falling back to a plain message.
export function errorMessage(error, fallback = 'Something went wrong. Please try again.') {
    const data = error?.response?.data;
    if (typeof data === 'string' && data.length < 200) return data;
    return data?.error || data?.message || fallback;
}

export function confirmAction({ title = 'Are you sure?', message = '', confirmLabel = 'Delete', variant = 'danger' } = {}) {
    return new Promise((resolve) => {
        window.dispatchEvent(new CustomEvent(CONFIRM_EVENT, { detail: { title, message, confirmLabel, variant, resolve } }));
    });
}

const ICONS = { success: 'fa-check-circle', error: 'fa-exclamation-circle', info: 'fa-info-circle' };

export function Toaster() {
    const [toasts, setToasts] = useState([]);
    const [confirm, setConfirm] = useState(null);

    useEffect(() => {
        let nextId = 1;
        const onToast = (e) => {
            const id = nextId++;
            setToasts((list) => [...list, { id, ...e.detail }]);
            const ttl = e.detail.type === 'error' ? 6000 : 3200;
            setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), ttl);
        };
        const onConfirm = (e) => setConfirm(e.detail);
        window.addEventListener(TOAST_EVENT, onToast);
        window.addEventListener(CONFIRM_EVENT, onConfirm);
        return () => {
            window.removeEventListener(TOAST_EVENT, onToast);
            window.removeEventListener(CONFIRM_EVENT, onConfirm);
        };
    }, []);

    const closeConfirm = (answer) => {
        if (confirm) confirm.resolve(answer);
        setConfirm(null);
    };

    return (
        <>
            <div className="ft-toasts" role="status" aria-live="polite">
                {toasts.map((t) => (
                    <div key={t.id} className={`ft-toast ft-toast-${t.type}`}>
                        <i className={`fa ${ICONS[t.type] || ICONS.info}`} aria-hidden="true"></i>
                        <span>{t.message}</span>
                        <button aria-label="Dismiss" onClick={() => setToasts((list) => list.filter((x) => x.id !== t.id))}>&times;</button>
                    </div>
                ))}
            </div>

            <Modal show={!!confirm} onHide={() => closeConfirm(false)} centered>
                <Modal.Body style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '22px' }}>
                    <span className="ft-confirm-icon"><i className="fa fa-trash-o" aria-hidden="true"></i></span>
                    <div>
                        <h5 style={{ fontWeight: 700, marginBottom: '6px' }}>{confirm?.title}</h5>
                        {confirm?.message && <div style={{ color: '#5b677a' }}>{confirm.message}</div>}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => closeConfirm(false)}>Cancel</Button>
                    <Button variant={confirm?.variant || 'danger'} onClick={() => closeConfirm(true)} autoFocus>
                        {confirm?.confirmLabel}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
